import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useRouter } from "next/router";

import { createClient } from "@/lib/supabase/component";
import type { User } from "@supabase/supabase-js";
import { setBugsnagUser, clearBugsnagUser } from "@/lib/bugsnag/client";
import { isCaptchaError, CAPTCHA_ERROR_MESSAGE } from "@/lib/turnstile/config";

import { toast } from "sonner";

type UserProviderProps = {
  children: React.ReactNode;
  /**
   * Perfil vindo do SSR (`pageProps.auth.profile`). Quando presente, o mount
   * hidrata o estado SEM refazer `auth.getUser()` + select em `clients` do
   * browser (2 roundtrips) — o `authUser` vem da session LOCAL (getSession).
   */
  initialClient?: ClientProfile | null;
};

export type ClientProfile = {
  id: string;
  name: string;
  email: string;
  role: "ADMIN" | "MANAGER" | "CLIENT";
  is_active: boolean;
  avatar: string | null;
  created_at: string;
  user_id: string;
  has_totp: boolean;
};

type AppUser = {
  authUser: User | null;
  client: ClientProfile | null;
};

type UserContextType = {
  user: AppUser | null;
  setUser: Dispatch<SetStateAction<AppUser | null>>;
  userLoading: boolean;
  logOut: () => void;
  logIn: (email: string, password: string, captchaToken?: string) => Promise<{ ok: boolean; hasTotp?: boolean; role?: string | null }>;
  updateUser: () => void;
};

const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  userLoading: false,
  logOut: () => {},
  logIn: async () => ({ ok: false }),
  updateUser: async () => {},
});

export const UserProvider = ({ children, initialClient }: UserProviderProps) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  const router = useRouter();
  const supabase = createClient();
  // Id do auth user já carregado — usado pra ignorar SIGNED_IN/TOKEN_REFRESHED
  // redundantes (mount/focus) sem refazer fetch.
  const loadedUserIdRef = useRef<string | null>(null);
  // O supabase-js dispara SIGNED_IN de forma síncrona DENTRO do
  // signInWithPassword — antes do logIn() registrar o usuário. Sem estas
  // guardas, o handler roda updateUser em paralelo e triplica as chamadas
  // (medido em produção: 3× clients + 2× getUser num único login).
  const authFlowInProgressRef = useRef(false);
  const updateUserInFlightRef = useRef(false);

  const updateUser = async () => {
    if (updateUserInFlightRef.current) return; // dedup: um por vez
    updateUserInFlightRef.current = true;
    try {
      await doUpdateUser();
    } finally {
      updateUserInFlightRef.current = false;
    }
  };

  const doUpdateUser = async () => {
    setUserLoading(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      loadedUserIdRef.current = null;
      setUser(null);
      setUserLoading(false);
      return;
    }

    const { data: clientRow } = await supabase
      .from("clients")
      .select("*")
      .eq("user_id", user.id)
      .single();

    loadedUserIdRef.current = user.id;
    setUser({
      authUser: user,
      client: clientRow ?? null,
    });
    setUserLoading(false);
  };

  /**
   * Mount: com `initialClient` (SSR), hidrata da session local (0 rede);
   * sem ele (ex: /login), cai no fluxo legado (updateUser).
   */
  const hydrateFromSsr = async (profile: ClientProfile) => {
    setUserLoading(true);
    const {
      data: { session },
    } = await supabase.auth.getSession(); // local — não vai à rede

    loadedUserIdRef.current = session?.user?.id ?? profile.user_id;
    setUser({
      authUser: (session?.user as User | undefined) ?? null,
      client: profile,
    });
    setUserLoading(false);
  };

  useEffect(() => {
    if (initialClient) {
      hydrateFromSsr(initialClient);
    } else {
      updateUser();
    }

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          // logIn() em andamento cuida do próprio fetch — evento é redundante.
          if (authFlowInProgressRef.current) return;
          // Mesmo usuário já carregado → evento redundante (mount/refresh/focus),
          // não refaz os 2 roundtrips.
          const incomingId = (session as { user?: { id?: string } } | null)
            ?.user?.id;
          if (incomingId && incomingId === loadedUserIdRef.current) return;
          updateUser();
        }
        if (event === "SIGNED_OUT") {
          loadedUserIdRef.current = null;
          setUser(null);
        }
      },
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      subscription.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.authUser) {
      setBugsnagUser(user.authUser.id, user.authUser.email);
    } else {
      clearBugsnagUser();
    }
  }, [user?.authUser?.id, user?.authUser?.email]);

  const logOut = async () => {
    await supabase.auth.signOut();
    loadedUserIdRef.current = null;
    setUser(null);
    router.replace("/login");
  };

  const logIn = async (
    email: string,
    password: string,
    captchaToken?: string
  ): Promise<{ ok: boolean; hasTotp?: boolean; role?: string | null }> => {
    authFlowInProgressRef.current = true;
    try {
      return await doLogIn(email, password, captchaToken);
    } finally {
      authFlowInProgressRef.current = false;
    }
  };

  const doLogIn = async (
    email: string,
    password: string,
    captchaToken?: string
  ): Promise<{ ok: boolean; hasTotp?: boolean; role?: string | null }> => {
    const { data: auth, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    });

    if (!auth?.user || error) {
      // Falha de captcha NÃO pode virar "senha inválida": o usuário com a senha
      // certa tentaria em loop sem entender o que está acontecendo.
      toast.error(isCaptchaError(error) ? CAPTCHA_ERROR_MESSAGE : "Email ou senha inválidos");
      return { ok: false };
    }

    const { data: clientRow } = await supabase
      .from("clients")
      .select("*")
      .eq("user_id", auth.user.id)
      .single();

    if (clientRow && clientRow.is_active === false) {
      await supabase.auth.signOut();
      toast.error("Conta desativada. Contate o administrador.");
      return { ok: false };
    }

    loadedUserIdRef.current = auth.user.id;
    setUser({
      authUser: auth.user,
      client: clientRow ?? null,
    });

    // Device touch + BLOCK enforcement moram no <FingerprintInit /> (_app),
    // que dispara quando o visitorId estiver pronto — sem duplicação aqui.
    return {
      ok: true,
      hasTotp: (clientRow as { has_totp?: boolean } | null)?.has_totp === true,
      role: clientRow?.role ?? null,
    };
  };

  return (
    <UserContext.Provider
      value={{ user, setUser, userLoading, logOut, logIn, updateUser }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
